/**
 * Loader du widget -- bundle en chat.js, charge par la page hote.
 *
 *   <script src="https://chat.exemple.fr/widget/chat.js"
 *           data-widget-key="wk_..." async></script>
 *
 * Trois responsabilites, et seulement celles-la :
 *
 *  1. PORTER L'IDENTITE EN FIRST-PARTY. Le chat est rendu dans une iframe servie
 *     par notre domaine, donc un contexte tiers pour la page hote : Safari/ITP
 *     cloisonne le localStorage d'une iframe tierce et Chrome supprime les
 *     cookies tiers. Le visitorToken est donc conserve ICI, dans la page hote, et
 *     transmis a l'iframe (fragment d'URL puis postMessage). Sans ce montage, la
 *     reconnaissance des visiteurs de retour ne survit pas aux navigateurs.
 *
 *  2. RESTER LEGER. Ce fichier est charge sur CHAQUE page du site du client : pas
 *     de socket.io, pas de zod, pas d'interface. Le poids et le travail sont dans
 *     frame.js, charge paresseusement a la premiere ouverture.
 *
 *  3. NE RIEN CASSER CHEZ L'HOTE. Styles enfermes dans un shadow root, conteneur
 *     unique en position fixe, aucune modification des styles ou du DOM du site.
 */

import type { AgentsAvailability, PublicSiteConfig, WidgetConfigResponse } from '@chat/shared';
import { LIMITS } from '@chat/shared';

import {
  FRAGMENT_KEYS,
  FRAME_SOURCE,
  hostEnvelope,
  isEnvelope,
} from './protocol.js';
import type {
  HostMessageBody,
  InitPayload,
  LocalConsent,
  LocalSession,
  PageInfo,
  VisitorHint,
} from './protocol.js';
import { safeHexColor, safeText } from './safe.js';
import { STORE_KEYS, createStore, readJson, writeJson } from './storage.js';
import type { WidgetStore } from './storage.js';

// ---------------------------------------------------------------------------
// API publique exposee a la page hote
// ---------------------------------------------------------------------------

interface ChatWidgetApi {
  readonly version: string;
  open(): void;
  close(): void;
  toggle(): void;
  isOpen(): boolean;
  /** Pre-remplit le profil visiteur (utilise par les plugins CMS, Phase 4). */
  setVisitor(hint: VisitorHint): void;
  /** Oublie l'identite locale. Le visiteur repart de zero a la prochaine visite. */
  reset(): void;
}

declare global {
  interface Window {
    ChatWidget?: ChatWidgetApi;
    /** Pose des le debut de l'amorcage : `ChatWidget` n'apparait qu'a la fin. */
    __chatWidgetBooting?: true;
    /** Safari anterieur a 14.1 n'expose l'API Web Audio que sous ce prefixe. */
    webkitAudioContext?: typeof AudioContext;
  }
}

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

const LOG_PREFIX = '[chat-widget]';
const CONFIG_TIMEOUT_MS = 8000;
const CONFIG_RETRY_DELAYS_MS = [1500, 4000];
const INIT_RETRY_INTERVAL_MS = 400;
const INIT_MAX_ATTEMPTS = 20;
const PAGE_DEBOUNCE_MS = 400;
const PENDING_PAGES_MAX = 10;

/**
 * Interactions de la page hote qui comptent comme « quelqu'un est la ».
 *
 * `pointermove` couvre souris, stylet et doigt d'un seul evenement. `scroll` est
 * indispensable et se voit mal : sur un article long, faire defiler EST l'unique
 * activite du lecteur -- sans lui, le visiteur le plus attentif serait celui
 * qu'on declarerait en veille le premier.
 *
 * `keydown` et pas `keypress` : les touches de defilement et les fleches ne
 * produisent pas de caractere, et ce sont justement celles d'un lecteur.
 */
const ACTIVITE_EVENEMENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'scroll',
  'wheel',
  'touchstart',
] as const;

/**
 * Periode minimale entre deux battements d'activite vers l'iframe.
 *
 * CE CHIFFRE EST LA SEULE CHOSE QUI SEPARE UN BATTEMENT D'UN FLUX. `pointermove`
 * se declenche des dizaines de fois par seconde ; sans ce plancher, bouger la
 * souris ferait traverser la frontiere postMessage a des centaines de messages
 * par minute, sur CHAQUE page de CHAQUE visiteur. Quinze secondes suffisent
 * amplement : le delai de veille le plus court accepte est de 30 s, donc deux
 * battements au moins tombent dans toute fenetre d'inactivite mesurable.
 */
const ACTIVITE_MIN_MS = 15_000;
const SVG_NS = 'http://www.w3.org/2000/svg';

function warn(message: string, detail?: unknown): void {
  if (detail === undefined) console.warn(`${LOG_PREFIX} ${message}`);
  else console.warn(`${LOG_PREFIX} ${message}`, detail);
}

// ---------------------------------------------------------------------------
// Lecture de la balise script
// ---------------------------------------------------------------------------

interface ScriptConfig {
  widgetKey: string;
  serverOrigin: string;
  /** Connecte le socket des le chargement, sans attendre une ouverture. */
  eagerConnect: boolean;
  /**
   * Jeton d'identite signe par la boutique, quand elle en depose un.
   *
   * `null` dans l'ecrasante majorite des cas : site sans module boutique, ou
   * visiteur non connecte a son compte. C'est la situation ORDINAIRE, et rien
   * d'autre ne change alors dans le parcours.
   *
   * LE LOADER NE LE LIT PAS, IL LE PORTE. Sa valeur est opaque : elle n'a de
   * sens que pour le serveur, qui seul detient de quoi verifier la signature.
   * Le loader ne peut donc rien en tirer, et c'est tres bien ainsi -- du code
   * qui tourne dans une page publique n'a aucun moyen de prouver quoi que ce
   * soit sur l'identite de qui la regarde.
   */
  identityToken: string | null;
}

/**
 * Retrouve la balise qui nous a charges.
 *
 * `document.currentScript` est la voie fiable, mais il vaut `null` pour un module
 * ES et dans quelques integrations qui reinjectent le script : d'ou le repli sur
 * la derniere balise portant data-widget-key.
 */
function findOwnScript(): HTMLScriptElement | null {
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement && current.hasAttribute('data-widget-key')) {
    return current;
  }

  const candidates = document.querySelectorAll<HTMLScriptElement>('script[data-widget-key]');
  return candidates.length > 0 ? (candidates[candidates.length - 1] ?? null) : null;
}

function readScriptConfig(): ScriptConfig | null {
  const script = findOwnScript();
  if (!script) {
    warn('aucune balise <script data-widget-key="..."> trouvee, widget non demarre');
    return null;
  }

  const widgetKey = (script.getAttribute('data-widget-key') ?? '').trim();
  if (widgetKey.length === 0) {
    warn('data-widget-key est vide');
    return null;
  }

  // L'origine du serveur est deduite de l'URL du script lui-meme : c'est la seule
  // valeur dont on soit sur, et elle evite au client de la saisir deux fois.
  // `data-server` reste utile derriere un CDN qui servirait chat.js ailleurs.
  const override = script.getAttribute('data-server');
  let serverOrigin: string | null = null;
  try {
    serverOrigin = new URL(override ?? script.src, window.location.href).origin;
  } catch {
    serverOrigin = null;
  }

  if (!serverOrigin || serverOrigin === 'null') {
    warn("impossible de determiner l'origine du serveur de chat");
    return null;
  }

  /* Borne des la lecture : au-dela, ce n'est pas un jeton, et le serveur le
     refuserait de toute facon apres l'avoir transporte pour rien. */
  const identity = (script.getAttribute('data-identity') ?? '').trim();

  return {
    widgetKey,
    serverOrigin,
    eagerConnect: script.getAttribute('data-eager-connect') === 'true',
    identityToken:
      identity.length > 0 && identity.length <= LIMITS.shopTokenMaxLength ? identity : null,
  };
}

// ---------------------------------------------------------------------------
// Amorcage : GET /api/widget/config
// ---------------------------------------------------------------------------

const POSITIONS = new Set<PublicSiteConfig['config']['position']>(['bottom-right', 'bottom-left']);
const AVAILABILITY_REASONS = new Set<AgentsAvailability['reason']>([
  'available',
  'outside_business_hours',
  'no_agent_online',
]);

/**
 * Normalise la reponse du serveur.
 *
 * Le loader ne peut pas embarquer zod (cf. l'en-tete du fichier) : il verifie donc
 * la forme a la main et remplace toute valeur douteuse par un repli, plutot que de
 * planter dans la page d'un client. L'iframe, elle, revalide integralement avec
 * zod -- et le serveur reste de toute facon la seule autorite.
 */
function normalizeConfig(raw: unknown): WidgetConfigResponse | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const root = raw as Record<string, unknown>;

  const site = root.site;
  if (typeof site !== 'object' || site === null) return null;
  const siteRecord = site as Record<string, unknown>;

  const siteId = typeof siteRecord.siteId === 'string' ? siteRecord.siteId : '';
  if (siteId.length === 0) return null;

  const rawConfig =
    typeof siteRecord.config === 'object' && siteRecord.config !== null
      ? (siteRecord.config as Record<string, unknown>)
      : {};
  const rawPrechat =
    typeof rawConfig.prechat === 'object' && rawConfig.prechat !== null
      ? (rawConfig.prechat as Record<string, unknown>)
      : {};
  const rawAvailability =
    typeof root.availability === 'object' && root.availability !== null
      ? (root.availability as Record<string, unknown>)
      : {};

  const position = rawConfig.position;

  return {
    site: {
      siteId,
      name: safeText(siteRecord.name, LIMITS.siteNameMaxLength, 'Chat'),
      timezone: safeText(siteRecord.timezone, 64, 'UTC'),
      // Repli sur `false` : ne pas promettre de reponse par email quand le
      // serveur ne se prononce pas (version anterieure, charge utile tronquee).
      repondParEmail: siteRecord.repondParEmail === true,
      config: {
        primaryColor: safeHexColor(rawConfig.primaryColor, '#2563eb'),
        headerTextColor: safeHexColor(rawConfig.headerTextColor, '#ffffff'),
        welcomeMessage: safeText(rawConfig.welcomeMessage, 500),
        offlineMessage: safeText(rawConfig.offlineMessage, 500),
        logoUrl:
          typeof rawConfig.logoUrl === 'string'
            ? rawConfig.logoUrl.slice(0, LIMITS.privacyPolicyUrlMaxLength)
            : null,
        privacyPolicyUrl:
          typeof rawConfig.privacyPolicyUrl === 'string'
            ? rawConfig.privacyPolicyUrl.slice(0, LIMITS.privacyPolicyUrlMaxLength)
            : null,
        /*
         * Replis alignes sur les valeurs par defaut du serveur, pour le cas ou
         * une version anterieure n'enverrait pas encore ces champs. Le repli de
         * `liveVisitors` est `true` A DESSEIN : c'est lui qui decide si le widget
         * se raccorde des le chargement de la page, donc si le visiteur apparait
         * dans la console. Un repli a `false` reproduirait exactement le defaut
         * qu'il corrige.
         */
        consentMode: rawConfig.consentMode === 'bloquant' ? 'bloquant' : 'discret',
        liveVisitors: rawConfig.liveVisitors !== false,
        typingPreview: rawConfig.typingPreview !== false,
        idleAfterSeconds: secondesDeVeille(rawConfig.idleAfterSeconds),
        /* `!== false` : un serveur anterieur n'envoie pas ce champ, et l'absence
           doit valoir « autorise ». Le serveur refuse de toute facon le depot
           quand le reglage est coupe. */
        visitorAttachments: rawConfig.visitorAttachments !== false,
        position: POSITIONS.has(position as PublicSiteConfig['config']['position'])
          ? (position as PublicSiteConfig['config']['position'])
          : 'bottom-right',
        prechat: {
          name: rawPrechat.name === true,
          email: rawPrechat.email === true,
          phone: rawPrechat.phone === true,
          // `!== false` et non `=== true` : un serveur anterieur n'envoie pas ce
          // champ, et l'absence doit valoir « obligatoire ».
          required: rawPrechat.required !== false,
          /* `=== true` cette fois, donc l'absence vaut « ne pas poser la
             question » : demander une reference de commande sur un site qui ne
             vend rien serait deroutant. Meme sens que le defaut du serveur. */
          order: rawPrechat.order === true,
        },
      },
    },
    availability: {
      online: rawAvailability.online === true,
      withinBusinessHours: rawAvailability.withinBusinessHours === true,
      agentsOnline:
        typeof rawAvailability.agentsOnline === 'number' && rawAvailability.agentsOnline >= 0
          ? Math.floor(rawAvailability.agentsOnline)
          : 0,
      reason: AVAILABILITY_REASONS.has(rawAvailability.reason as AgentsAvailability['reason'])
        ? (rawAvailability.reason as AgentsAvailability['reason'])
        : 'no_agent_online',
      nextOpeningAt:
        typeof rawAvailability.nextOpeningAt === 'string' ? rawAvailability.nextOpeningAt : null,
    },
    widgetVersion: safeText(root.widgetVersion, 32, __WIDGET_VERSION__),
  };
}

async function fetchWidgetConfig(
  serverOrigin: string,
  widgetKey: string,
): Promise<WidgetConfigResponse | null> {
  const url = new URL('/api/widget/config', serverOrigin);
  url.searchParams.set('key', widgetKey);

  for (let attempt = 0; ; attempt += 1) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), CONFIG_TIMEOUT_MS);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        // Aucun cookie : cet appel ne doit rien authentifier. La widgetKey
        // identifie le site, l'origine de la requete l'autorise.
        credentials: 'omit',
        mode: 'cors',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });

      /*
       * 429 ET 408 SONT DES REFUS PASSAGERS, PAS DEFINITIFS.
       *
       * Ils tombaient dans la branche « 4xx, inutile d'insister » avec les vraies
       * causes definitives (cle inconnue, origine refusee). Or l'amorcage est
       * plafonne par IP et consomme DEUX points par chargement de page : derriere
       * un NAT d'entreprise ou un operateur mobile, ce plafond est atteint par du
       * trafic parfaitement normal. Le widget n'etait alors pas monte DU TOUT sur
       * la page -- pas de bulle, pas de presence, rien -- alors qu'attendre
       * quelques secondes aurait suffi.
       *
       * `Retry-After` est respecte quand le serveur le pose ; sinon la boucle de
       * reprise deja en place fait son office.
       */
      const passager = response.status === 429 || response.status === 408;

      if (response.status >= 400 && response.status < 500 && !passager) {
        warn(`configuration refusee (HTTP ${response.status})`);
        return null;
      }

      if (passager) {
        const entete = Number.parseInt(response.headers.get('retry-after') ?? '', 10);
        const attente = Number.isFinite(entete) && entete > 0 ? Math.min(entete, 120) * 1000 : null;
        throw Object.assign(new Error(`HTTP ${response.status}`), { attenteMs: attente });
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const normalized = normalizeConfig((await response.json()) as unknown);
      if (!normalized) {
        warn('reponse de configuration inexploitable');
        return null;
      }
      return normalized;
    } catch (error) {
      const delay = CONFIG_RETRY_DELAYS_MS[attempt];
      if (delay === undefined) {
        /*
         * `fetch` rejette avec un TypeError aussi bien pour une panne reseau que
         * pour un blocage CORS -- le navigateur ne donne pas le detail au script.
         * Le diagnostic explicite fait gagner un temps considerable a l'integrateur,
         * car la cause de loin la plus frequente est l'absence d'en-tete
         * Access-Control-Allow-Origin sur /api/widget/config.
         */
        warn(
          `configuration inaccessible (${url.toString()}), widget non affiche. ` +
            "Verifier que la reponse porte un en-tete Access-Control-Allow-Origin pour l'origine " +
            `${window.location.origin}, et que le serveur de chat est joignable.`,
          error,
        );
        return null;
      }
      await new Promise((resolve) => window.setTimeout(resolve, delay));
    } finally {
      window.clearTimeout(timer);
    }
  }
}

// ---------------------------------------------------------------------------
// Petits utilitaires
// ---------------------------------------------------------------------------

function randomId(): string {
  const bytes = new Uint8Array(9);
  const webCrypto = window.crypto;
  if (webCrypto && typeof webCrypto.getRandomValues === 'function') {
    webCrypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  let out = '';
  for (const byte of bytes) out += byte.toString(16).padStart(2, '0');
  return out;
}

/** Icone construite element par element : aucun innerHTML dans tout le widget. */
function icon(className: string, paths: string[]): SVGSVGElement {
  const root = document.createElementNS(SVG_NS, 'svg');
  root.setAttribute('viewBox', '0 0 24 24');
  root.setAttribute('aria-hidden', 'true');
  root.setAttribute('focusable', 'false');
  root.setAttribute('class', className);
  for (const definition of paths) {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', definition);
    root.appendChild(path);
  }
  return root;
}

function pageSignature(page: PageInfo): string {
  return `${page.url}|${page.title ?? ''}`;
}

function currentPage(): PageInfo {
  return {
    url: window.location.href.slice(0, LIMITS.pageUrlMaxLength),
    title: document.title ? safeText(document.title, LIMITS.pageTitleMaxLength) || null : null,
    referrer: document.referrer ? document.referrer.slice(0, LIMITS.referrerMaxLength) : null,
  };
}

function readConsent(store: WidgetStore): LocalConsent | null {
  return readJson<LocalConsent>(store, STORE_KEYS.consent, (value) => {
    if (typeof value !== 'object' || value === null) return null;
    const record = value as Record<string, unknown>;
    if (typeof record.tracking !== 'boolean' || typeof record.geo !== 'boolean') return null;
    return {
      tracking: record.tracking,
      geo: record.geo,
      decidedAt: typeof record.decidedAt === 'string' ? record.decidedAt : new Date().toISOString(),
    };
  });
}

function readSession(store: WidgetStore): LocalSession {
  const fallback: LocalSession = {
    hasActiveConversation: false,
    unread: 0,
    open: false,
    updatedAt: new Date(0).toISOString(),
  };

  return (
    readJson<LocalSession>(store, STORE_KEYS.session, (value) => {
      if (typeof value !== 'object' || value === null) return null;
      const record = value as Record<string, unknown>;
      return {
        hasActiveConversation: record.hasActiveConversation === true,
        unread:
          typeof record.unread === 'number' && record.unread > 0 ? Math.min(record.unread, 99) : 0,
        open: record.open === true,
        updatedAt: typeof record.updatedAt === 'string' ? record.updatedAt : fallback.updatedAt,
      };
    }) ?? fallback
  );
}

// ---------------------------------------------------------------------------
// Notification sonore d'une reponse d'agent
// ---------------------------------------------------------------------------

/*
 * Carillon synthetise a la volee plutot qu'un fichier audio : un .mp3 serait une
 * requete de plus sur chaque page d'un site client et un actif de plus a servir,
 * pour deux notes. Deux sinusoides suffisent -- une sinusoide n'a aucune
 * harmonique, donc aucun timbre agressif dans la page de quelqu'un d'autre.
 *
 * TOUT echoue en silence ici. Un widget sans son reste un widget qui fonctionne ;
 * une exception ou un avertissement dans la console d'un site client, non.
 */

/** Gestes qui, chez tous les navigateurs, valent activation de la page hote. */
const AUDIO_GESTURES = ['pointerdown', 'keydown', 'touchstart'] as const;
/** Une rafale de reponses ne doit pas mitrailler le visiteur. */
const CHIME_MIN_INTERVAL_MS = 2500;
/** Discret par construction : ce son se joue chez le visiteur, pas chez nous. */
const CHIME_PEAK_GAIN = 0.05;
/** Sol5 puis Do6 : intervalle montant, entendu comme "quelque chose arrive". */
const CHIME_NOTES_HZ = [784, 1046.5];
const CHIME_NOTE_SECONDS = 0.13;
/**
 * Plancher de gain.
 *
 * `exponentialRampToValueAtTime` refuse la valeur zero -- une exponentielle ne
 * l'atteint jamais -- et leve une RangeError. D'ou cette cible strictement
 * positive, inaudible en pratique.
 */
const SILENT_GAIN = 0.0001;

let audioContext: AudioContext | null = null;
/** Vrai seulement une fois le contexte reellement debloque par un geste. */
let audioUnlocked = false;
let audioArmed = false;
let lastChimeAt = 0;

/**
 * Amorce le contexte audio. A N'APPELER QUE depuis un gestionnaire d'evenement
 * d'interaction : un AudioContext cree hors d'un geste nait suspendu, et seul un
 * `resume()` emis dans la pile d'appel d'un geste le debloque.
 */
function primeAudio(): void {
  try {
    if (!audioContext) {
      const AudioCtor: typeof AudioContext | undefined =
        window.AudioContext ?? window.webkitAudioContext;
      if (!AudioCtor) return;
      audioContext = new AudioCtor();
    }

    if (audioContext.state === 'running') {
      audioUnlocked = true;
      return;
    }

    /*
     * `Promise.resolve` enveloppe le retour : les Safari les plus anciens
     * renvoient `undefined` au lieu d'une promesse, et un `.then` direct y
     * leverait un TypeError.
     *
     * Et c'est precisement pourquoi l'etat REEL est relu a la resolution au lieu
     * d'etre suppose : sur ces memes moteurs, la promesse ainsi fabriquee se
     * resout immediatement sans rien dire du contexte. Se declarer debloque sur
     * cette seule foi ferait programmer les notes sur un contexte suspendu --
     * elles resteraient en file et sonneraient bien plus tard, sans rapport avec
     * quoi que ce soit. C'est exactement ce que ce drapeau doit empecher.
     */
    void Promise.resolve(audioContext.resume()).then(
      () => {
        audioUnlocked = audioContext !== null && audioContext.state === 'running';
      },
      () => undefined,
    );
  } catch {
    // Web Audio absent, refuse par une politique de securite, ou quota de
    // contextes atteint : on renonce au son.
  }
}

/**
 * Capte le premier geste du visiteur dans la page hote.
 *
 * Necessaire parce que le geste le plus probable -- l'envoi d'un message -- a
 * lieu DANS l'iframe : son activation ne se propage pas au document hote, et un
 * postMessage ne vaut pas interaction. En capture et en passif : on observe le
 * geste meme si un script du site arrete la propagation, sans jamais pouvoir
 * empecher l'action de la page.
 *
 * Arme seulement quand l'iframe est montee : ouvrir un fil audio sur chaque page
 * d'un site pour un visiteur qui n'ouvrira peut-etre jamais le chat serait du
 * gaspillage pur.
 */
function armAudio(): void {
  if (audioArmed) return;
  audioArmed = true;

  /*
   * DESARMEMENT SEULEMENT UNE FOIS LE CONTEXTE REELLEMENT DEBLOQUE.
   *
   * Tout evenement de cette liste ne vaut pas activation : Echap et les touches
   * de modification seules n'en sont pas, et `touchstart` ne compte pas partout.
   * Se retirer au premier evenement venu laisserait alors `audioUnlocked` a faux
   * pour le reste de la visite -- playChime rendrait la main sans un mot et le
   * carillon ne sonnerait jamais, sans rien dans la console pour l'expliquer.
   *
   * `resume()` etant asynchrone, le retrait a le plus souvent lieu au geste
   * suivant : trois ecouteurs passifs de plus le temps d'un clic.
   */
  const onGesture = (): void => {
    primeAudio();
    if (!audioUnlocked) return;
    for (const type of AUDIO_GESTURES) document.removeEventListener(type, onGesture, true);
  };

  for (const type of AUDIO_GESTURES) {
    document.addEventListener(type, onGesture, { capture: true, passive: true });
  }
}

function playNote(context: AudioContext, frequency: number, startAt: number): void {
  const oscillator = context.createOscillator();
  const envelope = context.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(frequency, startAt);

  // Attaque et extinction en rampe : une coupure nette produit un "clic" audible.
  envelope.gain.setValueAtTime(SILENT_GAIN, startAt);
  envelope.gain.exponentialRampToValueAtTime(CHIME_PEAK_GAIN, startAt + 0.015);
  envelope.gain.exponentialRampToValueAtTime(SILENT_GAIN, startAt + CHIME_NOTE_SECONDS);

  oscillator.connect(envelope);
  envelope.connect(context.destination);

  // Un noeud connecte reste reference par le graphe audio : sans cette
  // deconnexion, chaque notification en abandonnerait deux derriere elle.
  oscillator.onended = () => {
    oscillator.disconnect();
    envelope.disconnect();
  };

  oscillator.start(startAt);
  oscillator.stop(startAt + CHIME_NOTE_SECONDS + 0.02);
}

function playChime(): void {
  const context = audioContext;
  /*
   * Contexte jamais debloque : non seulement le navigateur refuserait le son,
   * mais les notes programmees resteraient en attente et se declencheraient
   * beaucoup plus tard, au premier geste du visiteur -- un carillon sans rapport
   * avec quoi que ce soit.
   */
  if (!context || !audioUnlocked) return;

  const now = Date.now();
  if (now - lastChimeAt < CHIME_MIN_INTERVAL_MS) return;
  lastChimeAt = now;

  try {
    // Un onglet passe en arriere-plan peut avoir suspendu le contexte (Safari) ;
    // la reprise est licite sans geste une fois le deblocage initial acquis.
    if (context.state === 'suspended') {
      void Promise.resolve(context.resume()).then(
        () => undefined,
        () => undefined,
      );
    }

    // Petite avance sur l'horloge audio : programmer a `currentTime` exact fait
    // rater le debut de l'enveloppe sur les moteurs a gros tampon.
    const start = context.currentTime + 0.02;
    CHIME_NOTES_HZ.forEach((frequency, index) => {
      playNote(context, frequency, start + index * CHIME_NOTE_SECONDS * 0.7);
    });
  } catch {
    // Contexte ferme par le navigateur entre-temps : rien a signaler.
  }
}

// ---------------------------------------------------------------------------
// Demarrage
// ---------------------------------------------------------------------------

function whenBodyReady(callback: () => void): void {
  if (document.body) {
    callback();
    return;
  }
  document.addEventListener('DOMContentLoaded', () => callback(), { once: true });
}

async function boot(): Promise<void> {
  // Deux integrations peuvent injecter le script (theme + plugin) : on ne monte
  // qu'une seule fois, sinon le visiteur voit deux bulles. Le drapeau est pose
  // avant le premier `await` : `window.ChatWidget`, lui, n'existe qu'a la fin du
  // montage et laisserait passer un second amorcage concurrent.
  if (window.ChatWidget || window.__chatWidgetBooting) return;
  window.__chatWidgetBooting = true;

  const scriptConfig = readScriptConfig();
  if (!scriptConfig) return;

  const { widgetKey, serverOrigin, eagerConnect, identityToken } = scriptConfig;
  const hostOrigin = window.location.origin;

  // postMessage exige une origine cible explicite ; une page en file:// a une
  // origine opaque ("null") avec laquelle aucun echange sur mesure n'est possible.
  if (!/^https?:$/.test(window.location.protocol)) {
    warn('le widget requiert une page servie en http(s)');
    return;
  }

  const store = createStore(widgetKey);
  const session = readSession(store);

  // La configuration est demandee AVANT tout affichage : couleurs, position,
  // message d'accueil et disponibilite doivent etre connus pour ne pas afficher
  // une bulle qui saute ou clignote.
  const config = await fetchWidgetConfig(serverOrigin, widgetKey);
  if (!config) return;

  whenBodyReady(() =>
    mount({
      config,
      session,
      store,
      widgetKey,
      serverOrigin,
      hostOrigin,
      eagerConnect,
      identityToken,
    }),
  );
}

interface MountOptions {
  config: WidgetConfigResponse;
  session: LocalSession;
  store: WidgetStore;
  widgetKey: string;
  serverOrigin: string;
  hostOrigin: string;
  eagerConnect: boolean;
  identityToken: string | null;
}

function mount(options: MountOptions): void {
  const { config, store, widgetKey, serverOrigin, hostOrigin } = options;
  const siteConfig = config.site.config;
  const channel = randomId();

  // ---------------------------------------------------------------- etat
  let open = false;
  let frameReady = false;
  let iframe: HTMLIFrameElement | null = null;
  let initAttempts = 0;
  let initTimer = 0;
  let unread = options.session.unread;
  let session: LocalSession = options.session;
  const outbox: HostMessageBody[] = [];
  const pendingPages: PageInfo[] = [];
  let lastReportedPage: string | null = null;

  const prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // -------------------------------------------------------- arbre du DOM
  const host = document.createElement('div');
  host.setAttribute('data-chat-widget', widgetKey);
  /*
   * Positionnement pose en inline `!important` : une declaration inline
   * importante gagne contre toute feuille du site hote, y compris un
   * `div { position: static !important }`. `pointer-events: none` sur le
   * conteneur plein ecran laisse la page hote entierement cliquable ; seuls la
   * bulle et le panneau reactivent les evenements.
   */
  host.style.cssText = [
    'position:fixed!important',
    'inset:0!important',
    'z-index:2147483000!important',
    'pointer-events:none!important',
    'margin:0!important',
    'padding:0!important',
    'border:0!important',
    'background:none!important',
    'display:block!important',
    'visibility:visible!important',
    'opacity:1!important',
    'transform:none!important',
    'filter:none!important',
    'max-width:none!important',
    'max-height:none!important',
  ].join(';');

  // Shadow root ferme : les styles du site hote n'entrent pas, les notres ne
  // sortent pas, et un script de la page ne peut pas manipuler notre DOM.
  let root: ShadowRoot | HTMLElement;
  if (typeof host.attachShadow === 'function') {
    root = host.attachShadow({ mode: 'closed' });
    const style = document.createElement('style');
    style.textContent = __LOADER_CSS__;
    root.appendChild(style);
  } else {
    // Repli pour un navigateur sans shadow DOM : la feuille n'utilise que des
    // classes prefixees `cw-`, elle peut donc vivre dans le document sans risque
    // de collision serieuse.
    root = host;
    const style = document.createElement('style');
    style.setAttribute('data-chat-widget-style', '');
    style.textContent = __LOADER_CSS__;
    document.head.appendChild(style);
  }

  const wrap = document.createElement('div');
  wrap.className = 'cw-wrap';
  wrap.dataset.position = siteConfig.position;
  wrap.dataset.open = 'false';
  wrap.style.setProperty('--cw-primary', siteConfig.primaryColor);
  wrap.style.setProperty('--cw-on-primary', siteConfig.headerTextColor);
  root.appendChild(wrap);

  const panel = document.createElement('div');
  panel.className = 'cw-panel';
  wrap.appendChild(panel);

  /*
   * Accroche : un conteneur non focusable qui porte DEUX vrais boutons. Un seul
   * bouton englobant avec une croix decorative a l'interieur serait impossible a
   * fermer au clavier sans ouvrir le chat -- et des boutons imbriques sont du HTML
   * invalide.
   */
  const teaser = document.createElement('div');
  teaser.className = 'cw-teaser';
  teaser.hidden = true;
  const teaserOpen = document.createElement('button');
  teaserOpen.type = 'button';
  teaserOpen.className = 'cw-teaser-open';
  const teaserText = document.createElement('span');
  teaserText.className = 'cw-teaser-text';
  teaserOpen.appendChild(teaserText);
  teaser.appendChild(teaserOpen);
  const teaserClose = document.createElement('button');
  teaserClose.type = 'button';
  teaserClose.className = 'cw-teaser-close';
  teaserClose.setAttribute('aria-label', "Masquer ce message d'accueil");
  teaserClose.textContent = '×';
  teaser.appendChild(teaserClose);
  wrap.appendChild(teaser);

  const bubble = document.createElement('button');
  bubble.type = 'button';
  bubble.className = 'cw-bubble';
  bubble.setAttribute('aria-haspopup', 'dialog');
  bubble.setAttribute('aria-expanded', 'false');
  bubble.appendChild(
    icon('cw-icon-open', [
      'M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5z',
    ]),
  );
  bubble.appendChild(icon('cw-icon-close', ['M18 6 6 18', 'M6 6l12 12']));
  const badge = document.createElement('span');
  badge.className = 'cw-badge';
  badge.hidden = true;
  bubble.appendChild(badge);
  wrap.appendChild(bubble);

  document.body.appendChild(host);

  // ---------------------------------------------------------- affichage
  function refreshBubbleLabel(): void {
    const label = open
      ? 'Fermer le chat'
      : config.availability.online
        ? `Ouvrir le chat avec ${config.site.name}`
        : 'Laisser un message';
    bubble.setAttribute('aria-label', unread > 0 ? `${label} (${unread} non lu)` : label);
    bubble.title = label;
  }

  function refreshBadge(): void {
    badge.hidden = unread === 0 || open;
    badge.textContent = unread > 99 ? '99+' : String(unread);
    refreshBubbleLabel();
  }

  function persistSession(patch: Partial<LocalSession>): void {
    session = { ...session, ...patch, updatedAt: new Date().toISOString() };
    writeJson(store, STORE_KEYS.session, session);
  }

  // ------------------------------------------------------- communication
  function post(body: HostMessageBody): void {
    const target = iframe?.contentWindow;
    if (!target || (!frameReady && body.type !== 'init')) {
      // File d'attente bornee : inutile d'accumuler l'historique complet d'une
      // navigation si l'iframe tarde a repondre.
      if (outbox.length < 32) outbox.push(body);
      return;
    }
    // Origine cible explicite, jamais "*" : sinon le message serait lisible par
    // n'importe quel document qui viendrait a occuper cette iframe.
    target.postMessage(hostEnvelope(channel, body), serverOrigin);
  }

  function flushOutbox(): void {
    const queued = outbox.splice(0, outbox.length);
    for (const body of queued) post(body);
  }

  function buildInitPayload(): InitPayload {
    return {
      widgetKey,
      serverOrigin,
      hostOrigin,
      visitorToken: store.get(STORE_KEYS.visitorToken),
      consent: readConsent(store),
      page: currentPage(),
      site: config.site,
      availability: config.availability,
      open,
      prefersReducedMotion,
      browserTimezone: readBrowserTimezone(),
      /* Transporte tel quel, dans l'init : sa valeur est fixee au rendu de la
         page, elle ne change pas tant que celle-ci vit. Un message a part
         n'apporterait qu'un aller-retour de plus. */
      identityToken: options.identityToken,
    };
  }

  function scheduleInit(): void {
    window.clearTimeout(initTimer);
    if (frameReady || initAttempts >= INIT_MAX_ATTEMPTS) return;
    initAttempts += 1;
    post({ type: 'init', payload: buildInitPayload() });
    initTimer = window.setTimeout(scheduleInit, INIT_RETRY_INTERVAL_MS);
  }

  /**
   * Monte l'iframe. Appelee a la premiere ouverture -- ou des le chargement si une
   * conversation est en cours, pour que le visiteur continue de recevoir les
   * reponses de l'agent en changeant de page.
   */
  function mountFrame(): void {
    if (iframe) return;

    const url = new URL('/api/widget/frame', serverOrigin);
    url.searchParams.set('key', widgetKey);
    url.searchParams.set('v', config.widgetVersion);

    /*
     * Jeton, canal et origine hote passent par le FRAGMENT et non par la query :
     * un fragment n'est jamais envoye au serveur. Le jeton visiteur n'apparait
     * donc ni dans les journaux d'acces, ni dans un en-tete Referer.
     */
    const fragment = new URLSearchParams();
    fragment.set(FRAGMENT_KEYS.channel, channel);
    fragment.set(FRAGMENT_KEYS.hostOrigin, hostOrigin);
    const token = store.get(STORE_KEYS.visitorToken);
    if (token) fragment.set(FRAGMENT_KEYS.token, token);
    url.hash = fragment.toString();

    iframe = document.createElement('iframe');
    iframe.src = url.toString();
    iframe.title = 'Fenetre de discussion';
    iframe.setAttribute('aria-label', 'Fenetre de discussion');
    /*
     * allow-same-origin est necessaire (l'iframe parle a notre propre serveur) ;
     * l'absence d'allow-top-navigation reste l'apport utile du bac a sable : meme
     * compromise, l'iframe ne peut pas rediriger le site du client.
     *
     * allow-downloads : sans lui, Chrome (depuis la version 83) bloque tout
     * telechargement lance dans l'iframe. Le bouton « Telecharger » d'une piece
     * jointe ne faisait alors rien, sans un message pour le visiteur.
     */
    iframe.setAttribute(
      'sandbox',
      'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads',
    );
    /*
     * L'iframe repond 'ready' UNIQUEMENT apres avoir recu un init valide : c'est
     * le loader qui mene la poignee de main, en reessayant tant qu'il n'a pas de
     * reponse. Un postMessage envoye avant que le document de l'iframe n'ait pose
     * son ecouteur est perdu sans avertissement -- d'ou les tentatives repetees.
     */
    iframe.addEventListener('load', () => {
      initAttempts = 0;
      scheduleInit();
    });
    panel.appendChild(iframe);
    scheduleInit();

    // Des lors qu'une conversation peut vivre, le son peut avoir a sonner : c'est
    // le moment d'attendre le geste qui debloquera le contexte audio.
    armAudio();
  }

  /**
   * Une reponse d'agent vient d'arriver dans l'iframe.
   *
   * Le loader decide seul s'il faut la signaler : lui seul connait l'etat du
   * panneau dans la page hote ET la visibilite de l'onglet.
   */
  function notifyAgentMessage(): void {
    // Panneau deplie et onglet au premier plan : le visiteur voit deja le message
    // arriver, un son de plus ne serait que du bruit dans la page d'un client.
    if (open && document.visibilityState === 'visible') return;
    playChime();
  }

  // -------------------------------------------------------- ouverture
  function setOpen(next: boolean, focus: boolean): void {
    if (open === next) return;
    open = next;
    wrap.dataset.open = next ? 'true' : 'false';
    bubble.setAttribute('aria-expanded', next ? 'true' : 'false');

    if (next) {
      mountFrame();
      teaser.hidden = true;
      unread = 0;
      post({ type: 'open', payload: { focus } });
      if (focus && iframe) iframe.focus();
    } else {
      post({ type: 'close', payload: undefined });
      // Le focus revient sur la bulle SEULEMENT si la fermeture vient d'une action
      // de l'utilisateur (croix, touche Echap). Un appel programme de
      // ChatWidget.close() ne doit pas voler le focus a la page hote.
      if (focus) bubble.focus();
    }

    refreshBadge();
    persistSession({ open: next, unread });
  }

  /*
   * `primeAudio` est appele ICI, dans la pile d'appel du clic : c'est la seule
   * facon de debloquer le contexte audio. Le geste suivant le plus probable --
   * l'envoi d'un message -- a lieu dans l'iframe et n'activerait pas ce document.
   */
  bubble.addEventListener('click', () => {
    primeAudio();
    setOpen(!open, true);
  });

  teaserOpen.addEventListener('click', () => {
    primeAudio();
    setOpen(true, true);
  });

  teaserClose.addEventListener('click', () => {
    teaser.hidden = true;
    // Refus memorise : reproposer l'accroche a chaque page serait du harcelement.
    store.set(STORE_KEYS.teaserDismissed, '1');
    bubble.focus();
  });

  // Echap ferme le panneau meme si le focus est reste dans la page hote.
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && open) setOpen(false, true);
  });

  // ------------------------------------------- messages venant de l'iframe
  window.addEventListener('message', (event: MessageEvent) => {
    /*
     * Trois verifications, toutes obligatoires : l'origine (le message vient bien
     * de notre serveur), la fenetre emettrice (c'est bien NOTRE iframe et pas une
     * autre iframe du meme domaine), et le nonce de canal. Un postMessage accepte
     * sans ces controles est une porte ouverte sur la page du client.
     */
    if (event.origin !== serverOrigin) return;
    if (!iframe || event.source !== iframe.contentWindow) return;
    if (!isEnvelope(event.data, FRAME_SOURCE, channel)) return;

    const { type, payload } = event.data;
    const record =
      typeof payload === 'object' && payload !== null ? (payload as Record<string, unknown>) : {};

    switch (type) {
      case 'ready': {
        frameReady = true;
        window.clearTimeout(initTimer);
        flushOutbox();
        for (const page of pendingPages.splice(0, pendingPages.length)) {
          post({ type: 'page', payload: page });
        }
        if (open) post({ type: 'open', payload: { focus: false } });
        break;
      }

      case 'visitor-token': {
        const value = record.visitorToken;
        if (typeof value === 'string' && value.length > 0 && value.length <= 128) {
          store.set(STORE_KEYS.visitorToken, value);
        }
        break;
      }

      case 'consent': {
        if (typeof record.tracking === 'boolean' && typeof record.geo === 'boolean') {
          const consent: LocalConsent = {
            tracking: record.tracking,
            geo: record.geo,
            decidedAt:
              typeof record.decidedAt === 'string' ? record.decidedAt : new Date().toISOString(),
          };
          writeJson(store, STORE_KEYS.consent, consent);
        }
        break;
      }

      case 'unread': {
        const count = record.count;
        unread = typeof count === 'number' && count > 0 ? Math.min(Math.floor(count), 999) : 0;
        refreshBadge();
        persistSession({ unread });
        break;
      }

      case 'session': {
        persistSession({ hasActiveConversation: record.hasActiveConversation === true });
        break;
      }

      case 'availability': {
        config.availability.online = record.online === true;
        refreshBubbleLabel();
        break;
      }

      case 'request-close': {
        setOpen(false, true);
        break;
      }

      case 'agent-message': {
        notifyAgentMessage();
        break;
      }

      default:
        break;
    }
  });

  // ------------------------------------------------- suivi de navigation
  function reportPage(): void {
    const page = currentPage();
    const signature = pageSignature(page);
    if (signature === lastReportedPage) return;
    lastReportedPage = signature;

    if (frameReady) {
      post({ type: 'page', payload: page });
      return;
    }

    // Iframe pas encore prete : on garde les dernieres pages pour que l'agent
    // voie le parcours du visiteur des la connexion.
    pendingPages.push(page);
    if (pendingPages.length > PENDING_PAGES_MAX) pendingPages.shift();
  }

  let pageTimer = 0;
  function schedulePageReport(): void {
    // Une SPA appelle souvent pushState puis met le titre a jour : la
    // temporisation evite de remonter la page avec l'ancien titre.
    window.clearTimeout(pageTimer);
    pageTimer = window.setTimeout(reportPage, PAGE_DEBOUNCE_MS);
  }

  // La page d'arrivee part deja dans l'init (champ `page`) : on l'enregistre comme
  // deja remontee, sinon le serveur la compterait deux fois.
  lastReportedPage = pageSignature(currentPage());
  installNavigationTracking(schedulePageReport);

  document.addEventListener('visibilitychange', () => {
    const visible = document.visibilityState === 'visible';
    post({ type: 'visibility', payload: { visible } });
    // Revenir sur l'onglet EST une interaction : sans ce battement, un visiteur
    // qui reprend sa lecture resterait annonce absent jusqu'a son premier
    // mouvement de souris, alors qu'il vient de revenir sous nos yeux.
    if (visible) signalerActivite();
  });

  // --------------------------------------------------------- veille visiteur
  /*
   * PRESENCE ET ACTIVITE SONT DEUX CHOSES.
   *
   * Un onglet ouvert renouvelle son bail de presence indefiniment : sans ce
   * battement, quelqu'un parti depuis une heure est annonce a la console
   * exactement comme celui qui lit en ce moment. L'agent qui aborde le premier
   * ecrit dans le vide et ne l'apprend jamais.
   *
   * LA DECISION N'EST PAS PRISE ICI. Ce bloc ne fait que dire « il vient de se
   * passer quelque chose » ; c'est l'iframe qui tient le minuteur, parce qu'elle
   * seule connait le delai configure pour ce site. Le loader est charge sur
   * chaque page du site client : tout ce qu'on ne lui demande pas est autant de
   * poids en moins sur des pages qui ne sont pas les notres.
   */
  let dernierBattement = 0;

  function signalerActivite(): void {
    const maintenant = Date.now();
    if (maintenant - dernierBattement < ACTIVITE_MIN_MS) return;
    dernierBattement = maintenant;
    post({ type: 'activity', payload: undefined });
  }

  for (const nom of ACTIVITE_EVENEMENTS) {
    /*
     * `passive` : ces ecouteurs ne doivent JAMAIS retarder le defilement de la
     * page d'un client. Sans ce drapeau, le navigateur attend de savoir si nous
     * allons appeler preventDefault avant de faire defiler -- un widget de tchat
     * qui rend le site saccade serait desinstalle, et il aurait raison de
     * l'etre. `capture` : un site qui arrete la propagation sur son propre
     * contenu ne nous rendrait pas aveugles.
     */
    document.addEventListener(nom, signalerActivite, { passive: true, capture: true });
  }

  // ------------------------------------------------------------ accroche
  const welcome = siteConfig.welcomeMessage;
  if (welcome.length > 0 && store.get(STORE_KEYS.teaserDismissed) !== '1') {
    teaserText.textContent = welcome;
    // Petit differe : une accroche qui surgit pendant le chargement de la page
    // est percue comme une publicite.
    window.setTimeout(() => {
      if (!open && !session.hasActiveConversation) teaser.hidden = false;
    }, 1200);
  }

  refreshBadge();

  /*
   * QUAND LE WIDGET SE RACCORDE AU SERVEUR.
   *
   * Trois raisons de le faire immediatement, panneau ferme. L'iframe reste
   * invisible et hors de l'ordre de tabulation dans tous les cas.
   *
   *  - `liveVisitors` (reglage du site, actif par defaut) : LE MANQUE ICI ETAIT
   *    UN DEFAUT. Sans lui, la connexion n'avait lieu qu'au CLIC sur la bulle,
   *    et un visiteur qui parcourait le site sans ouvrir la discussion
   *    n'apparaissait ni dans la console des agents ni sur le mur. Toute la
   *    chaine de presence existait cote serveur -- bail Redis, diffusion aux
   *    consoles, page courante, etat du panneau -- et rien, dans un navigateur,
   *    ne la declenchait jamais. Le symptome (« je ne vois pas les visiteurs en
   *    ligne ») ne pointait vers aucun de ces composants, qui fonctionnaient
   *    tous.
   *
   *  - une conversation deja ouverte : les messages de l'agent doivent arriver
   *    meme si le visiteur n'a pas rouvert le panneau ;
   *
   *  - `data-eager-connect="true"` : demande explicite de l'integrateur, qui
   *    reste prioritaire sur le reglage du site.
   */
  if (options.eagerConnect || siteConfig.liveVisitors || session.hasActiveConversation) {
    mountFrame();
  }
  if (session.open && session.hasActiveConversation) setOpen(true, false);

  // ---------------------------------------------------------- API publique
  const api: ChatWidgetApi = {
    version: __WIDGET_VERSION__,
    open: () => setOpen(true, true),
    close: () => setOpen(false, false),
    toggle: () => setOpen(!open, true),
    isOpen: () => open,
    /*
     * Le profil pre-rempli n'entraine PAS le chargement de l'iframe : un plugin
     * qui appelle setVisitor sur chaque page ne doit pas annuler le chargement
     * paresseux. Le message part en file d'attente et sera livre a l'ouverture.
     */
    setVisitor: (hint) => post({ type: 'visitor-hint', payload: sanitizeHint(hint) }),
    reset: () => {
      store.remove(STORE_KEYS.visitorToken);
      store.remove(STORE_KEYS.consent);
      store.remove(STORE_KEYS.session);
      store.remove(STORE_KEYS.teaserDismissed);
      post({ type: 'reset', payload: undefined });
    },
  };

  window.ChatWidget = api;
}

/**
 * Delai de veille annonce par le serveur, ramene dans les bornes.
 *
 * Le loader ne s'en sert pas lui-meme -- c'est l'iframe qui tient le minuteur --
 * mais il transporte la configuration jusqu'a elle : une valeur absente (serveur
 * d'une version anterieure) ou hors bornes doit etre corrigee AVANT de traverser
 * la frontiere, pas apres.
 */
function secondesDeVeille(brut: unknown): number {
  if (typeof brut !== 'number' || !Number.isFinite(brut)) return LIMITS.idleAfterSecondsDefault;
  const arrondi = Math.round(brut);
  if (arrondi < LIMITS.idleAfterSecondsMin) return LIMITS.idleAfterSecondsMin;
  if (arrondi > LIMITS.idleAfterSecondsMax) return LIMITS.idleAfterSecondsMax;
  return arrondi;
}

function sanitizeHint(hint: VisitorHint): VisitorHint {
  const out: VisitorHint = {};
  if (typeof hint.name === 'string') out.name = hint.name.slice(0, LIMITS.visitorNameMaxLength);
  if (typeof hint.email === 'string') out.email = hint.email.slice(0, LIMITS.visitorEmailMaxLength);
  if (typeof hint.phone === 'string') out.phone = hint.phone.slice(0, LIMITS.visitorPhoneMaxLength);
  return out;
}

function readBrowserTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}

/**
 * Suivi de navigation, y compris dans une application monopage.
 *
 * pushState et replaceState ne declenchent aucun evenement : sans ce patch, un
 * site en React ou Vue ne remonterait qu'une seule page vue pour toute la visite.
 * Les originaux sont conserves et rappeles, pour ne casser aucun routeur.
 */
function installNavigationTracking(report: () => void): void {
  const patch = (method: 'pushState' | 'replaceState'): void => {
    const original = window.history[method];
    if (typeof original !== 'function') return;

    window.history[method] = function patched(
      this: History,
      ...args: Parameters<History['pushState']>
    ): void {
      const result = original.apply(this, args);
      try {
        report();
      } catch {
        // Le suivi ne doit jamais faire echouer la navigation du site hote.
      }
      return result;
    };
  };

  patch('pushState');
  patch('replaceState');
  window.addEventListener('popstate', report);
  window.addEventListener('hashchange', report);
}

void boot();
